import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PickFilterDef, PickFilterValue, PickFilterValues } from '../types';
import { PickFilterContext } from './pickFilterContextState';
import { mergeInitialPickFilterValues } from './pickFilterValues';

export interface PickFilterProviderProps {
  children: ReactNode;
  filters?: readonly PickFilterDef[];
  initialValues?: Partial<PickFilterValues>;
  values?: PickFilterValues;
  onValuesChange?: (values: PickFilterValues) => void;
}

export function PickFilterProvider({
  children,
  filters = [],
  initialValues,
  values: controlledValues,
  onValuesChange,
}: PickFilterProviderProps) {
  const queryClient = useQueryClient();
  const isControlled = controlledValues != null && onValuesChange != null;

  const [uncontrolledValues, setUncontrolledValues] = useState<PickFilterValues>(() =>
    mergeInitialPickFilterValues(filters, initialValues)
  );

  useEffect(() => {
    if (!isControlled && initialValues != null) {
      setUncontrolledValues((prev) =>
        mergeInitialPickFilterValues(filters, { ...prev, ...initialValues })
      );
    }
  }, [isControlled, initialValues, filters]);

  const values = isControlled ? controlledValues : uncontrolledValues;

  const setFilterValue = useCallback(
    (id: string, value: PickFilterValue) => {
      if (isControlled) {
        onValuesChange({ ...controlledValues, [id]: value });
      } else {
        setUncontrolledValues((prev) => ({ ...prev, [id]: value }));
      }
    },
    [isControlled, controlledValues, onValuesChange]
  );

  useEffect(() => {
    for (const filter of filters) {
      if (!filter.prefetch) continue;
      void queryClient.prefetchQuery({
        queryKey: filter.queryKey,
        queryFn: filter.queryFn,
        staleTime: filter.staleTime ?? 0,
      });
    }
  }, [queryClient, filters]);

  const contextValue = useMemo(
    () => ({ filters, values, setFilterValue }),
    [filters, values, setFilterValue]
  );

  return <PickFilterContext.Provider value={contextValue}>{children}</PickFilterContext.Provider>;
}
